import {useMemo} from 'react'
import Select, {StylesConfig} from 'react-select'
import FormErrorMessage from '../../../../components/FormErrorMessage/FormErrorMessage'
import {SingleValue} from 'react-select'
import {useTheme} from '../../../../contexts/ThemeContext'

interface CountrySelectorProps {
  countryOptions: {label: string; value: string}[]
  stateOptions?: {label: string; value: string}[] | []
  handleRegionFormControlOnChange: (type: string, data: {label: string; value: string}) => void
  formField: {
    label: string
    placeholder: string
    required: boolean
    id: string
    errorMessage?: string | null
    state: {
      label: string
      placeholder: string
      required: boolean
      id: string
      errorMessage?: string | null
    }
  }
  storedCountryData: SingleValue<{label: string; value: string}>
  storedStateData: SingleValue<{label: string; value: string}>
  flagFormErrors?: boolean
}

export default function RegionFormControl({
  handleRegionFormControlOnChange,
  formField,
  storedCountryData,
  storedStateData,
  flagFormErrors,
  countryOptions,
  stateOptions = [],
}: CountrySelectorProps) {
  const {effectiveTheme} = useTheme()
  const selectStyles = useMemo<StylesConfig<{label: string; value: string}, false>>(() => {
    const isDark = effectiveTheme === 'dark'
    return {
      control: (base, state) => ({
        ...base,
        backgroundColor: isDark ? '#0f172a' : '#ffffff',
        borderColor: state.isFocused ? '#0ea5e9' : isDark ? '#475569' : '#cbd5e1',
        boxShadow: state.isFocused ? '0 0 0 1px #0ea5e9' : 'none',
        '&:hover': {
          borderColor: '#0ea5e9',
        },
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: isDark ? '#0f172a' : '#ffffff',
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#0284c7' : state.isFocused ? '#1e293b' : 'transparent',
        color: state.isSelected || state.isFocused ? '#f8fafc' : isDark ? '#cbd5e1' : '#334155',
      }),
      singleValue: (base) => ({
        ...base,
        color: isDark ? '#e2e8f0' : '#1f2937',
      }),
      input: (base) => ({
        ...base,
        color: isDark ? '#e2e8f0' : '#1f2937',
      }),
      placeholder: (base) => ({
        ...base,
        color: isDark ? '#94a3b8' : '#94a3b8',
      }),
    }
  }, [effectiveTheme])

  const validatedCountryErrorMessage = useMemo(() => {
    if (typeof formField.errorMessage === 'string' && formField.errorMessage.length > 0) {
      return formField.errorMessage
    }
    return null
  }, [formField.errorMessage])

  const validatedStateErrorMessage = useMemo(() => {
    if (
      typeof formField.state.errorMessage === 'string' &&
      formField.state.errorMessage.length > 0
    ) {
      return formField.state.errorMessage
    }
    return null
  }, [formField.state])

  return (
    <div className="mb-5">
      <div className={stateOptions?.length > 0 ? 'mb-5' : ''}>
        <label
          className={`block ${flagFormErrors && formField.errorMessage ? 'text-red-600 dark:text-red-300' : 'text-sky-500 dark:text-sky-400'} text-xs mb-1`}
          htmlFor={formField.id}
        >
          {formField.label}
          <span className="text-red-600 pl-1">{formField.required ? '*' : ''}</span>
        </label>
        <Select
          options={countryOptions}
          value={storedCountryData}
          onChange={(countryData) => {
            if (countryData) handleRegionFormControlOnChange('country', countryData)
          }}
          isSearchable
          styles={selectStyles}
        />
        {flagFormErrors && validatedCountryErrorMessage && (
          <FormErrorMessage message={validatedCountryErrorMessage} />
        )}
      </div>

      {stateOptions.length > 0 && (
        <div>
          <label
            className={`block ${flagFormErrors && formField?.state?.errorMessage ? 'text-red-600 dark:text-red-300' : 'text-sky-500 dark:text-sky-400'} text-xs mb-1`}
            htmlFor={formField?.state?.id}
          >
            {formField?.state?.label}
            <span className="text-red-600 pl-1">{formField?.state?.required ? '*' : ''}</span>
          </label>
          <Select
            options={stateOptions}
            value={storedStateData}
            onChange={(stateData) => {
              if (stateData) handleRegionFormControlOnChange('state', stateData)
            }}
            isSearchable
            styles={selectStyles}
          />
          {flagFormErrors && validatedStateErrorMessage && (
            <FormErrorMessage message={validatedStateErrorMessage} />
          )}
        </div>
      )}
    </div>
  )
}
